/**
 * Resolves the best deep-link URL for a notification based on its type and data payload.
 * Falls back to the stored action_url, then a sensible default.
 */

interface NotificationLike {
  type: string;
  data?: Record<string, any>;
  action_url?: string;
  category?: string;
}

export function resolveNotificationUrl(n: NotificationLike, role?: string): string {
  const d = n.data || {};
  const isCourier = role === 'courier';
  const isStaff = role === 'admin' || role === 'support';

  // Helper: prefer a stored action_url over a generic fallback whenever it
  // looks like a real deep link (i.e. not just a portal root). This keeps
  // server-emitted, more specific URLs (e.g. /support-portal/live-chat,
  // /admin/couriers/:id, /courier/jobs) from being clobbered by generic
  // category landings when type-specific data fields are missing.
  const useActionUrlOr = (fallback: string): string => {
    if (n.action_url && n.action_url.startsWith('/')) return n.action_url;
    return fallback;
  };

  switch (n.type) {
    // ── Booking lifecycle ──
    case 'booking_created':
    case 'booking_confirmed':
    case 'booking_cancelled':
    case 'booking_completed':
      if (d.booking_id) return isCourier ? `/courier/bookings/${d.booking_id}` : `/account/bookings/${d.booking_id}`;
      break;

    // ── Bids / Offers ──
    case 'new_bid':
    case 'bid_accepted':
    case 'bid_declined':
      if (d.request_id) return `/account/requests/${d.request_id}`;
      if (d.bid_id) return `/account/requests`;
      break;

    case 'match_offer_new':
    case 'match_offer_accepted':
    case 'match_offer_removed':
    case 'match_offer_expired':
      if (d.booking_id) return `/courier/bookings/${d.booking_id}`;
      if (d.match_offer_id) return `/courier/match-offers/${d.match_offer_id}`;
      // Server typically emits action_url=/courier/jobs (the marketplace).
      return useActionUrlOr('/courier/match-offers');

    // ── Messages ──
    case 'question_received':
    case 'answer_received':
    case 'new_message':
      if (d.conversation_id) return isCourier ? `/courier/messages/${d.conversation_id}` : `/account/messages/${d.conversation_id}`;
      if (d.request_id) return isCourier ? `/courier/messages` : `/account/messages`;
      break;

    // ── Trips ──
    case 'trip_created':
    case 'trip_updated':
    case 'trip_completed':
      if (d.trip_id) return `/courier/trips/${d.trip_id}`;
      return useActionUrlOr('/courier/trips');

    // ── Payments ──
    case 'payment_received':
    case 'payment_failed':
      if (d.booking_id) return isCourier ? `/courier/bookings/${d.booking_id}` : `/account/bookings/${d.booking_id}`;
      break;

    // ── Disputes ──
    // The dispute thread itself is the real destination — prefer it over
    // the booking page whenever we have a dispute_id, for every event type
    // the server emits (see supabase/functions/kb-ops/recovery/dispute-notifications).
    case 'dispute_opened':
    case 'dispute_resolved':
    case 'dispute_status_changed':
    case 'dispute_message':
    case 'dispute_assigned':
    case 'dispute_evidence_added':
    case 'dispute_sla_breach':
      if (d.dispute_id) {
        if (role === 'support') return `/support-portal/disputes/${d.dispute_id}`;
        if (role === 'admin') return `/admin/disputes/${d.dispute_id}`;
        return isCourier ? `/courier/disputes/${d.dispute_id}` : `/account/disputes/${d.dispute_id}`;
      }
      if (d.booking_id) return isCourier ? `/courier/bookings/${d.booking_id}` : `/account/bookings/${d.booking_id}`;
      return useActionUrlOr(isCourier ? '/courier/disputes' : '/account/disputes');

    // ── Shipment / Delivery lifecycle ──
    // Server emits both `shipment_*` and `delivery_*` types — handle both.
    case 'shipment_collected':
    case 'shipment_transit':
    case 'shipment_in_transit':
    case 'shipment_ready':
    case 'shipment_delivered':
    case 'shipment_update':
    case 'delivery_pickup':
    case 'delivery_transit':
    case 'delivery_complete':
      if (d.booking_id) return isCourier ? `/courier/bookings/${d.booking_id}` : `/account/bookings/${d.booking_id}`;
      break;

    // ── System / Staff ──
    case 'system_announcement':
      // Most system announcements ship a precise action_url
      // (e.g. /courier/profile, /admin/couriers/:id). Honor it.
      return useActionUrlOr(isCourier ? '/courier/notifications' : '/account/notifications');
    case 'staff_new_ticket':
      if (d.ticket_id) return `/support-portal/tickets/${d.ticket_id}`;
      return useActionUrlOr('/support-portal');
    case 'staff_new_livechat':
      if (d.conversation_id) return `/support-portal/live-chat?conversation=${d.conversation_id}`;
      return useActionUrlOr('/support-portal/live-chat');
  }

  // Fall back to stored action_url
  if (n.action_url) return n.action_url;

  // Category-based fallback
  if (n.category === 'booking') return isCourier ? '/courier/bookings' : '/account/bookings';
  if (n.category === 'trip') return '/courier/trips';
  if (n.category === 'payment') return isCourier ? '/courier/bookings' : '/account/bookings';

  if (isStaff) return '/support-portal';
  return isCourier ? '/courier/notifications' : '/account/notifications';
}

/**
 * Returns quick-action buttons for a notification, with proper deep-link URLs.
 */
export function getNotificationActions(
  n: NotificationLike,
  role?: string,
  t?: (key: string) => string
): Array<{ label: string; url: string; primary?: boolean }> {
  const tr = (key: string, fallback: string) => t ? t(key) : fallback;
  const url = resolveNotificationUrl(n, role);
  const d = n.data || {};
  const isCourier = role === 'courier';

  switch (n.type) {
    case 'new_bid':
    case 'match_offer_new':
      return [
        { label: tr('portal.notifications.viewDetails', 'View Details'), url, primary: true },
      ];

    case 'booking_created':
    case 'booking_confirmed':
    case 'booking_completed':
      return [
        { label: tr('portal.notifications.viewBooking', 'View Booking'), url, primary: true },
      ];

    case 'question_received':
    case 'answer_received':
    case 'new_message':
      return [
        { label: tr('portal.notifications.reply', 'Reply'), url, primary: true },
      ];

    case 'payment_received':
    case 'payment_failed':
      return [
        { label: tr('portal.notifications.viewBooking', 'View Booking'), url, primary: true },
      ];

    case 'dispute_opened':
    case 'dispute_resolved':
      return [
        { label: tr('portal.notifications.viewDetails', 'View Details'), url, primary: true },
      ];

    case 'delivery_pickup':
    case 'delivery_transit':
    case 'delivery_complete':
      return [
        { label: tr('portal.notifications.viewBooking', 'View Booking'), url, primary: true },
      ];

    default:
      if (url) return [{ label: tr('portal.notifications.view', 'View'), url, primary: true }];
      return [];
  }
}